import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {EAutocompleteOption} from 'webapp-lib/Components';

import casusBackend, {CasusSearchItem} from '@lexian/api/casus-api';
import {assignableCasusFilters, useCasusByIds} from '_/store/searchCasus';
import {casusStateItems, CasusStateTranslation} from '_/utilities/casus.utils';
import {requiredRule} from '_/utilities/form';
import {EFCheckboxGroup} from '_/utilities/form/controlled-components';

import {
  AutocompleteBase,
  AutocompleteFilter,
  paginatedLoader,
} from '../AutocompleteBase/AutocompleteBase';

import {
  CasusSearchFilterColumn,
  CasusSearchFilterRow,
} from './CasusAutocomplete.styles';
import {
  CasusAutocompleteProps,
  CasusStateFilter,
} from './CasusAutocomplete.types';
import {CASUS_CATEGORIES, mapToOption} from './CasusAutocomplete.utils';

type CasusFilterState = {casusState: CasusStateFilter[]};

const DEFAULT_CASUS_STATES: CasusStateFilter[] = ['ACTIVE'];

export function CasusAutocomplete({
  filters = assignableCasusFilters,
  searchFiltersPersistenceKey,
  onSelectedCasusIsFilteredOut,
  disableUnassignableCasus = false,
  onCasusChange,
  recommendedCasusIds = [],
  categories = CASUS_CATEGORIES,
  ...rest
}: CasusAutocompleteProps): React.ReactElement {
  const {t} = useTranslation('common');
  const searchFilter = useCasusSearchFilter({
    persistenceKey: searchFiltersPersistenceKey,
  });
  const [casusStates, setCasusStates] = useState(DEFAULT_CASUS_STATES);
  const trackCasusStates = useCallback(
    (filterState: CasusFilterState | undefined) =>
      setCasusStates(filterState?.casusState ?? DEFAULT_CASUS_STATES),
    []
  );
  const recommendedOptions = useCasusByIds(recommendedCasusIds, {
    ...filters,
    stateFilters: casusStates,
  }).data;

  return (
    <AutocompleteBase
      {...rest}
      additionalItems={recommendedOptions}
      categories={categories}
      filter={searchFilter}
      labelInfo={
        disableUnassignableCasus
          ? t('casusAutocomplete.optionDisabledReason')
          : undefined
      }
      loadItems={paginatedLoader({
        endpointFunction: casusBackend.searchAllCasus,
        getPageItemFromResponse: (body) => body.casus,
        params: (filterState: CasusFilterState | undefined) => ({
          ...filters,
          stateFilters: filterState?.casusState ?? DEFAULT_CASUS_STATES,
        }),
      })}
      mapOptions={(items, context) =>
        mapCasusOptions({
          items,
          recommendedOptions,
          disableUnassignableCasus,
          ...context,
        })
      }
      reloadTrigger={filters}
      toOption={(casus) => mapToOption(casus, disableUnassignableCasus)}
      noOptionFiltering
      onFilterStateChange={trackCasusStates}
      onSelectedItemChange={onCasusChange}
      onSelectedItemMissing={onSelectedCasusIsFilteredOut}
    />
  );
}

function useCasusSearchFilter({
  persistenceKey,
}: {
  persistenceKey: string;
}): AutocompleteFilter<CasusFilterState> {
  const {t} = useTranslation('common');
  return {
    defaultState: {casusState: DEFAULT_CASUS_STATES},
    dialogHeader: t('casusAutocomplete.searchFilters.dialog.header'),
    persistenceKey: `casusAutocomplete.searchFilters.${persistenceKey}`,
    toText: ({casusState}) =>
      casusState.map((state) => CasusStateTranslation[state]).join(', '),
    warningText: t('casusAutocomplete.filteredOutWarningText'),
    renderFields: (control) => (
      <>
        <CasusSearchFilterRow>
          <CasusSearchFilterColumn>
            <EFCheckboxGroup
              control={control}
              items={casusStateItems}
              label={t(
                'casusAutocomplete.searchFilters.dialog.casusStateFilter'
              )}
              name='casusState'
              rules={requiredRule}
            />
          </CasusSearchFilterColumn>
        </CasusSearchFilterRow>
      </>
    ),
  };
}

function mapCasusOptions({
  items,
  recommendedOptions,
  disableUnassignableCasus,
  searchQuery,
  hasMoreBefore,
}: {
  items: CasusSearchItem[];
  recommendedOptions: CasusSearchItem[];
  disableUnassignableCasus: boolean;
  searchQuery: string;
  hasMoreBefore: boolean;
}): EAutocompleteOption[] {
  const filteredRecommendedOptions = recommendedOptions.filter((it) =>
    it.displayNameWithClient.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredItems = filterOutRecommendedOptions(
    items,
    filteredRecommendedOptions
  );

  return [
    ...(hasMoreBefore ? [] : filteredRecommendedOptions),
    ...filteredItems,
  ].map((it) => mapToOption(it, disableUnassignableCasus));
}

function filterOutRecommendedOptions(
  items: CasusSearchItem[],
  recommendedOptions: CasusSearchItem[]
): CasusSearchItem[] {
  if (recommendedOptions.length === 0) {
    return items;
  }
  const recommendedIds = recommendedOptions.map((it) => it.id);
  return items.filter((item) => !recommendedIds.includes(item.id));
}
